import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { subjects, type SubjectStatus } from '@/api/client';
import { ActionMenu, ActionItem } from '@/components/ui/ActionMenu';
import { Modal } from '@/components/ui/Modal';
import {
  SUBJECT_EDGES,
  SUBJECT_TRANSITION_LABELS as TRANSITION_LABEL,
  humanSubjectStatus,
} from '@/lib/subjectStatus';
import { errorText } from '@/lib/permissionError';

/**
 * CTMS-005 — the subject status dropdown. Shows ONLY the edges legal from the
 * current status, so a move the backend would refuse is never offered.
 *
 * The vocabulary, the edges and the labels all come from lib/subjectStatus.ts,
 * which mirrors the backend's statusGate.ts and is parity-tested against it.
 * They used to be declared inline here, which is how the FE came to be missing
 * edges the BE allowed.
 */

interface Props {
  subject: { subject_id: string; status: SubjectStatus };
}

export function SubjectStatusActions({ subject }: Props) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<SubjectStatus | null>(null);
  const [reason, setReason] = useState('');

  const transitions = SUBJECT_EDGES[subject.status] ?? [];

  const mutation = useMutation({
    mutationFn: (to: SubjectStatus) =>
      subjects.transitionStatus(subject.subject_id, {
        to,
        reason: reason.trim() ? reason.trim() : undefined,
      }),
    onSuccess: (res) => {
      toast.success(
        res.no_op
          ? `Subject already ${humanSubjectStatus(res.status)}`
          : `Subject status set to ${humanSubjectStatus(res.status)}`,
      );
      setPending(null);
      setReason('');
      qc.invalidateQueries({ queryKey: ['subject', subject.subject_id] });
      qc.invalidateQueries({ queryKey: ['subjects'] });
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to update status')),
  });

  if (transitions.length === 0) {
    // Terminal state — nothing to do, render a passive label so the header
    // doesn't have an orphaned hover target.
    return (
      <span className="text-[10px] uppercase tracking-wide text-ink-muted">
        Terminal state
      </span>
    );
  }

  return (
    <>
      <ActionMenu label="Change subject status">
        {transitions.map((target) => (
          <ActionItem
            key={target}
            onClick={() => {
              setReason('');
              setPending(target);
            }}
            danger={target === 'withdrawn' || target === 'screen_fail'}
          >
            {TRANSITION_LABEL[target]}
          </ActionItem>
        ))}
      </ActionMenu>

      <Modal
        open={pending !== null}
        onOpenChange={(b) => {
          if (!b && !mutation.isPending) {
            setPending(null);
            setReason('');
          }
        }}
        title={pending ? TRANSITION_LABEL[pending] : ''}
        description={
          pending
            ? [
                `This will transition the subject from "${humanSubjectStatus(subject.status)}" to "${humanSubjectStatus(pending)}". The change is logged to the audit trail.`,
                // Active is set automatically on the first conducted visit, so
                // say so rather than letting a coordinator think it is a step
                // they were supposed to have taken.
                pending === 'active'
                  ? ' Active is normally set automatically when the first visit is conducted — you only need this if that visit happened outside the system.'
                  : '',
              ].join('')
            : undefined
        }
        width={520}
      >
        {pending && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate(pending);
            }}
            className="space-y-4"
          >
            <div>
              <label className="label text-xs" htmlFor="status-reason">
                Reason{' '}
                {pending === 'withdrawn'
                  ? '(required for discontinuation)'
                  : pending === 'screen_fail'
                    ? '(required for a screen failure)'
                    : '(optional)'}
              </label>
              <textarea
                id="status-reason"
                className="input min-h-[80px]"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={2000}
                required={pending === 'withdrawn' || pending === 'screen_fail'}
                placeholder="Recorded against the audit event for this transition."
              />
              <p className="mt-1 text-[10px] text-ink-muted">
                Max 2000 characters. Visible in Compliance &gt; Audit Trail.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-ghost"
                disabled={mutation.isPending}
                onClick={() => {
                  setPending(null);
                  setReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={
                  pending === 'withdrawn' || pending === 'screen_fail'
                    ? 'btn-danger'
                    : 'btn-primary'
                }
                disabled={mutation.isPending}
              >
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
